import { TimesheetRow } from '@sit/api-types';
import {
  GetApiDimensionsResponse,
  GetApiTimesheetsByUuidRowsResponse,
  useInvalidateGetTimesheetRows,
  usePutTimesheetRowDimensionValues,
} from '@sit/client-shared';
import { useGetDimensions } from '@web/api/dimensions/use-get-dimensions';
import { queryClient } from '@web/query-client';
import { useShowDepartmentLocation } from '@web/redux/hooks';
import { addRequest, removeRequest } from '@web/redux/timesheet-status-slice';
import { setSyncStatus } from '@web/redux/timesheets-slice';
import { useCallback, useRef } from 'react';
import { loadAutofilledDimensionValues, loadAutofilledDimensionValuesToRemove } from '../../containers/Timesheet/helpers';
import api from '../../helpers/api';
import { selectRowAutofill } from '../../redux/selectors';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { AutofillStoreRow } from '../../types/redux/stores/autofill.types';

// QUERY TIMESHEET ROWS

interface UseQueryTimesheetRowsParams {
  timesheetId: string;
}

interface GetTimesheetRowsParams {
  token: string | null;
  params: UseQueryTimesheetRowsParams;
}

const getTimesheetRows = async ({ token, params: { timesheetId, ...params } }: GetTimesheetRowsParams) => {
  return await api.send<TimesheetRow[]>({
    url: `/timesheets/${timesheetId}/rows`,
    method: 'GET',
    cache: 'default',
    token,
    params,
    skipToast: true,
  });
};

export const fetchQueryTimesheetRows = (params: UseQueryTimesheetRowsParams, token: string) =>
  queryClient.fetchQuery(['timesheets', params.timesheetId, 'rows'], () => getTimesheetRows({ token, params }));

// MUTATE TIMESHEET ROW DIMENSIONS

export interface TimesheetDimensions {
  client: string | null;
  item: string | null;
  project: string | null;
  task: string | null;
  timeType: string | null;
  department: string | null;
  location: string | null;
  costType: string | null;
  employeePosition: string | null;
  laborClass: string | null;
  laborShift: string | null;
  laborUnion: string | null;
}
interface PutTimesheetRowDimensions {
  data: TimesheetDimensions;
}

interface DimensionCache {
  previousOtherValues: string[];
  dimensions: GetApiDimensionsResponse | undefined;
  autofills: AutofillStoreRow | null;
  sendDepartmentData: boolean;
}

const calculateTimesheetRowDimensions = (
  { data: rawData }: PutTimesheetRowDimensions,
  { previousOtherValues, dimensions, autofills, sendDepartmentData }: DimensionCache,
) => {
  const { department, location, costType, employeePosition, laborClass, laborShift, laborUnion, ...data } = rawData;
  const otherValues = loadAutofilledDimensionValues({
    department: sendDepartmentData ? department : null,
    location: sendDepartmentData ? location : null,
    costType,
    employeePosition,
    laborClass,
    laborShift,
    laborUnion,
    dimensions,
    rowAutofill: autofills,
  });

  const newData = {
    ...data,
    otherValues,
    removeValues: loadAutofilledDimensionValuesToRemove({
      otherValues,
      prevOtherValues: previousOtherValues,
    }),
  };
  return { otherValues, newData };
};

const usePreviousRowValues = (rowData: Pick<GetApiTimesheetsByUuidRowsResponse[number], 'timesheet_id' | 'id' | 'dimensionValues'>) => {
  const sendDepartmentData = useShowDepartmentLocation(rowData.timesheet_id);
  const autofills = useAppSelector((state) => selectRowAutofill(state, rowData.id));
  const { data: dimensions } = useGetDimensions({
    timesheetId: rowData.timesheet_id,
  });

  const {
    departmentId: department = null,
    locationId: location = null,
    costTypeId: costType = null,
    employeePositionId: employeePosition = null,
    laborClassId: laborClass = null,
    laborShiftId: laborShift = null,
    laborUnionId: laborUnion = null,
  } = rowData.dimensionValues || {};

  const otherValues = loadAutofilledDimensionValues({
    department: sendDepartmentData ? department : null,
    location: sendDepartmentData ? location : null,
    costType,
    employeePosition,
    laborClass,
    laborShift,
    laborUnion,
    dimensions,
    rowAutofill: autofills,
  });

  const previousOtherValues = useRef<string[]>(otherValues);

  return previousOtherValues;
};

export const usePutTimesheetDimensions = (rowData: GetApiTimesheetsByUuidRowsResponse[number]) => {
  const params = {
    timesheetId: rowData.timesheet_id,
    id: rowData.id,
  };

  const { data: dimensions } = useGetDimensions({
    timesheetId: params.timesheetId,
  });
  const autofills = useAppSelector((state) => selectRowAutofill(state, params.id));
  const sendDepartmentData = useShowDepartmentLocation(params.timesheetId);
  const dispatch = useAppDispatch();
  const invalidateRows = useInvalidateGetTimesheetRows();

  const previousOtherValues = usePreviousRowValues(rowData);

  const { mutateAsync, ...rest } = usePutTimesheetRowDimensionValues();

  const updateDimensions = useCallback(
    async (payload: TimesheetDimensions) => {
      dispatch(addRequest({ name: 'TIMESHEET_ROW_DIMENSIONS_UPDATE' }));
      const { otherValues, newData } = calculateTimesheetRowDimensions(
        { data: payload },
        {
          previousOtherValues: previousOtherValues.current,
          dimensions,
          autofills,
          sendDepartmentData,
        },
      );
      await mutateAsync({
        pathParams: {
          timesheetRowId: params.id,
          id: params.timesheetId,
        },
        body: newData,
      });
      previousOtherValues.current = otherValues;

      dispatch(setSyncStatus({ timesheetId: params.timesheetId, synced: false }));
      dispatch(removeRequest({ name: 'TIMESHEET_ROW_DIMENSIONS_UPDATE' }));
      invalidateRows(params.timesheetId);
    },
    [dispatch, mutateAsync, dimensions, params.id, previousOtherValues, sendDepartmentData, autofills, invalidateRows, params.timesheetId],
  );

  return {
    updateDimensions,
    ...rest,
  };
};
